import {observer} from "mobx-react";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import {FormField} from "../../../../../components/form-field/form-field";
import {Input} from "../../../../../components/inputs/input/input";
import {IOption, SelectDropdown} from "../../../../../components/inputs/select-dropdown/select-dropdown";
import Card from "../../../../../components/layouts/card/card";
import {IValidationGroup} from "../../../../../hooks/validation-group/validation-group-hook";
import SubscriptionPlanEditorStore, {
    INSTALLMENT_BREAKDOWN_TYPE_OPTIONS,
    PLAN_CHARGE_TYPE_OPTIONS,
    PLAN_CHARGE_TYPES,
} from "../../../stores/subscription-plan-editor-store";
import {SingleValue} from "react-select";
import GridLine, {BackgroundColor} from "../../../../../components/layouts/grid-line-layout/grid-line/grid-line";
import React, { useCallback } from "react";
import ControlGroup from "../../../../../components/layouts/control-group/control-group";
import IconPlus from "../../../../../components/icons/icon-plus/icon-plus";
import {Button} from "../../../../../components/inputs/button/button";
import {BUTTON_VARIANTS} from "../../../../../components/inputs/button-variants";
import InstallmentLine from "../installment-note/installment-line";
import ListLayout from "../../../../../components/layouts/list-layout/list-layout";
import { INSTALLMENTS_BREAKDOWN_TYPES } from "../../../stores/installment-list-manager";
import {Note, NOTE_TYPE} from "../../../../../components/note/note";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";

export interface ICardPricingProps {
    validationGroup: IValidationGroup;
    store?: SubscriptionPlanEditorStore;
}

const CardPricing: React.FC<ICardPricingProps> = observer(({validationGroup, store}) => {

    const installmentsList = store?.installmentsList;

    const selectedPlanType = (type?: PLAN_CHARGE_TYPES): IOption|undefined => {
        type = type ?? PLAN_CHARGE_TYPES.RECURRING;
        return PLAN_CHARGE_TYPE_OPTIONS.find((option) => {
            return option.value === type;
        });
    }

    const onPlanTypeChange = (value: SingleValue<IOption>) => {
        if (value) {
            store?.setType(value.value as PLAN_CHARGE_TYPES);
        }
    }

    const selectedInstallmentBreakdownType = (type?: INSTALLMENTS_BREAKDOWN_TYPES): IOption|undefined => {
        const installmentType = type;

        return INSTALLMENT_BREAKDOWN_TYPE_OPTIONS.find((option) => {
            return option.value === installmentType;
        });
    }

    const onInstallmentTypeChange = (value: SingleValue<IOption>) => {
        if(value) {
            installmentsList?.changeInstallmentsBreakdownType(value.value as INSTALLMENTS_BREAKDOWN_TYPES);
        }
    }

    const validatePrice = useCallback(() => {
        return store?.validatePrice() || { isValid:false, messages:[] };
    }, [store]);

    return <Card
        disabled={store?.isInUse}
        toolbar={{
            title: "Pricing"
        }}
    >
        <ListLayout>
            <div>
                <GridLine
                    withoutSidePadding={true}
                    withBorder={false}
                    backgroudColor={BackgroundColor.LIGHT}
                    settings={
                        {
                            columns: [{width: 1}, {width: 1}, {width: 1}]
                        }
                    }
                    columns={
                        [
                            <FormField label='Price' required>
                                <Input
                                    type='number'
                                    required
                                    value={store?.price}
                                    onChange={store?.setPrice}
                                    validationGroup={validationGroup}
                                    validationCallback={validatePrice}
                                    adornment={<FontBody1>$</FontBody1>}
                                    temp
                                />
                            </FormField>,
                            <FormField label='Returns quota' required>
                                <Input
                                    type='number'
                                    min={0}
                                    max={1000000}
                                    required
                                    temp
                                    value={store?.rma_quota}
                                    onChange={store?.setRmaQuota}
                                    validationGroup={validationGroup}
                                />
                            </FormField>,
                            <FormField label='Price per excess return' required>
                                <Input
                                    type='number'
                                    temp
                                    min={0}
                                    required
                                    value={store?.price_per_extra_rma}
                                    onChange={store?.setPricePerExtraRma}
                                    validationGroup={validationGroup}
                                    adornment={<FontBody1>$</FontBody1>}
                                />
                            </FormField>,
                        ]
                    }
                />
                <GridLine
                    withoutSidePadding={true}
                    withBorder={false}
                    backgroudColor={BackgroundColor.LIGHT}
                    settings={
                        {
                            columns: [{width: 1}, {width: 1}, {width: 1}]
                        }
                    }
                    columns={
                        [
                            <FormField label='Quota consumption' required>
                                <SelectDropdown
                                    value={selectedPlanType(store?.type)}
                                    options={PLAN_CHARGE_TYPE_OPTIONS}
                                    onChange={onPlanTypeChange}
                                />
                            </FormField>,

                            !!store?.hasConsumptionPeriod() &&
                            (
                                <FormField
                                    label="Consumption period"
                                    required
                                >
                                    <Input type="number"
                                        temp
                                        min={0}
                                        required
                                        value={store?.period}
                                        onChange={(v) => store?.setPeriod(v)}
                                        validationGroup={validationGroup}
                                    />
                                </FormField>
                            ),

                            store?.type === PLAN_CHARGE_TYPES.LONG_TERM &&
                            <FormField label='Installment Breakdown' required>
                                <SelectDropdown
                                    value={selectedInstallmentBreakdownType(installmentsList?.installmentBreakdownType)}
                                    options={INSTALLMENT_BREAKDOWN_TYPE_OPTIONS}
                                    onChange={onInstallmentTypeChange}
                                />
                            </FormField>,
                        ]
                    }
                />
                {store?.type === PLAN_CHARGE_TYPES.LONG_TERM && installmentsList?.installmentBreakdownType === INSTALLMENTS_BREAKDOWN_TYPES.EVEN &&
                    <div>
                        <GridLine
                            withoutSidePadding={true}
                            withBorder={false}
                            backgroudColor={BackgroundColor.LIGHT}
                            settings={
                                {
                                    columns: [{width: 1}, {width: 1}, {width: 1}]
                                }
                            }
                            columns={
                                [
                                    <FormField label='Installment size' required>
                                        <div className="installment-size-input-wrapper" style={{backgroundColor: 'var(--color-neutral-5)'}}>
                                            <Input type="number"
                                                   adornment={<FontBody1>$</FontBody1>}
                                                   min={0}
                                                   disabled={true}
                                                   value={installmentsList?.getEvenInstallmentsPrice()}
                                                   validationGroup={validationGroup}
                                            />
                                        </div>
                                    </FormField>,

                                    <FormField
                                        label="Installments count"
                                        required
                                    >
                                        <Input type="number"
                                            temp
                                            min={0}
                                            max={store?.period ?? 0}
                                            step={1}
                                            onChange={v => installmentsList?.setNumberOfEvenInstallments(v)}
                                            value={installmentsList?.getNumberOfEvenInstallments()}
                                            validationGroup={validationGroup}
                                            validationCallback={installmentsList?.validateEvenInstallmentsCount}
                                        />
                                    </FormField>
                                ]
                            }
                        />
                    </div>
                }
            </div>

            {store?.type === PLAN_CHARGE_TYPES.LONG_TERM && installmentsList?.installmentBreakdownType === INSTALLMENTS_BREAKDOWN_TYPES.CUSTOM &&
                <div>
                    <ListLayout>
                        {
                            store?.installmentsList.getInstallments().map((installmentStore, index) =>
                                <InstallmentLine key={index} index={index} installmentStore={installmentStore}/>
                            )
                        }
                    </ListLayout>
                    {installmentsList?.installmentBreakdownType === INSTALLMENTS_BREAKDOWN_TYPES.CUSTOM &&
                        <Button
                            variant={BUTTON_VARIANTS.Text}
                            text={'ADD NEW INSTALLMENT'}
                            leftIcon={IconPlus}
                            onClick={() => installmentsList?.addCustomInstallment()}
                        />
                    }
                </div>
            }

            {store?.type === PLAN_CHARGE_TYPES.LONG_TERM && installmentsList?.installmentBreakdownType === INSTALLMENTS_BREAKDOWN_TYPES.ABSOLUTE &&
                <div>
                    <ListLayout>
                        { store?.installmentsList.getInstallments().length > 0 &&
                            <Note
                            type={NOTE_TYPE.INFORMATION}
                            body={
                                    <FontBody2>
                                        {`Absolute installment dates considered as UTC dates`}
                                     </FontBody2>
                                 }
                            />
                        }
                        {
                            store?.installmentsList.getInstallments().map((installmentStore, index) =>
                                <InstallmentLine key={index} index={index} installmentStore={installmentStore}/>
                            )
                        }
                    </ListLayout>
                    {installmentsList?.installmentBreakdownType === INSTALLMENTS_BREAKDOWN_TYPES.ABSOLUTE &&
                        <Button
                            variant={BUTTON_VARIANTS.Text}
                            text={'ADD NEW INSTALLMENT'}
                            leftIcon={IconPlus}
                            onClick={() => installmentsList?.addAbsoluteInstallment()}
                        />
                    }
                </div>
            }
        </ListLayout>
        <ControlGroup>
            <FormField
                tooltip={"This text will appear on Shopify's \"Approve billing page\""}
                label='Capped amount'
                required
            >
                <Input
                    type='number'
                    temp
                    required
                    value={store?.capped_amount}
                    onChange={store?.setCappedaAmount}
                    validationGroup={validationGroup}
                    validationCallback={store?.validateCappedAmount}
                    adornment={<FontBody1>$</FontBody1>}
                />
            </FormField>
        </ControlGroup>
        <ControlGroup>
            <FormField
                label='Trial days'
                required
            >
                <Input
                    type='number'
                    temp
                    min={0}
                    required
                    value={store?.trialDays}
                    onChange={store?.setTrialDays}
                    validationGroup={validationGroup}
                    disabled={store?.isTrialDaysDisabled}
                />
            </FormField>
        </ControlGroup>

    </Card>
});

export default CardPricing;
